<script lang="ts">
enum Position {
  Left = "left",
  Right = "right"
}
</script>

<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import TgProgress from "@/abc-dsk/components/Progress/TgProgress.vue";
import { Color, ColorVariant, Gradient } from "@/abc-dsk/options";

type PositionType = `${Position}`;

const props = withDefaults(
  defineProps<{
    color: Color;
    gradient?: Gradient;
    whiteText?: boolean;
    steps: number;
    progress: number;
    progressPosition: PositionType;
  }>(),
  {
    whiteText: false,
    gradient: undefined
  }
);

const { t } = useI18n();

const colorVariant = computed<ColorVariant | undefined>((): ColorVariant | undefined => {
  if (props.gradient) {
    return undefined;
  }

  if (![Color.Blurple, Color.Gold].includes(props.color)) {
    return ColorVariant.V50;
  }

  return ColorVariant.V25;
});
</script>

<template>
  <div class="achievement-item-progress-bar">
    <span
      v-if="progressPosition === Position.Right"
      aria-hidden="true"
      class="achievement-item-progress-bar__text"
      :class="whiteText ? 'achievement-item-progress-bar__text--white' : undefined"
    >
      {{ progress }}/{{ steps }}
    </span>
    <TgProgress
      :color="color"
      :color-variant="colorVariant"
      :gradient="gradient"
      :screen-reader-label="t('vocab.achievements.item.progressBarLabel')"
      :value="progress"
      :max="steps"
    />
    <span
      v-if="progressPosition === Position.Left"
      aria-hidden="true"
      class="achievement-item-progress-bar__text"
      :class="whiteText ? 'achievement-item-progress-bar__text--white' : undefined"
    >
      {{ progress }}/{{ steps }}
    </span>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.achievement-item-progress-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  &__text {
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.3125rem;

    &--white {
      color: @colors[@white];
    }
  }
}
</style>
